import { getInvoiceDisplayItems, getSubItemLabel } from '@jobos/shared';
import type { BreakdownLineItemWithSubItems, SubItemCategory, Rates, SubItemTypeMetaMap } from '@jobos/shared';

const CSV_HEADER = [
  '*InvoiceNo',
  '*Customer',
  '*InvoiceDate',
  '*DueDate',
  'Terms',
  'Location',
  'Memo',
  'Item(Product/Service)',
  'ItemDescription',
  'ItemQuantity',
  'ItemRate',
  '*ItemAmount',
  'Shipping address',
  'Ship via',
  'Shipping date',
  'Tracking no.',
  'Shipping Charge',
  'Service Date',
].join(',');

function escapeCsvValue(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

function formatDateDDMMYYYY(date: Date): string {
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
}

function isoToDateDDMMYYYY(isoDate: string): string {
  const parts = (isoDate.split('T')[0] ?? '').split('-');
  if (parts.length !== 3) return '';
  const [yyyy, mm, dd] = parts;
  return `${dd}/${mm}/${yyyy}`;
}

interface QuickBooksCsvParams {
  lineItems: BreakdownLineItemWithSubItems[];
  rates: Rates;
  typeMeta: SubItemTypeMetaMap;
  categories: SubItemCategory[];
  clientName: string;
  jobCode: string;
  today?: Date; // for testing
}

export function generateQuickBooksCsv({
  lineItems,
  rates,
  typeMeta,
  categories,
  clientName,
  jobCode,
  today,
}: QuickBooksCsvParams): string {
  const now = today ?? new Date();
  const dueDate = new Date(now);
  dueDate.setMonth(dueDate.getMonth() + 1);

  const invoiceDate = formatDateDDMMYYYY(now);
  const dueDateStr = formatDateDDMMYYYY(dueDate);

  const sorted = [...lineItems]
    .filter((li) => li.category.toLowerCase() !== 'disclaimer')
    .sort((a, b) => a.sortOrder - b.sortOrder);

  const rows = sorted.map((li, index) => {
    // Calculate display amount by summing invoice display items
    const displayItems = getInvoiceDisplayItems(li.subItems, rates, typeMeta, categories);
    const totalAmount = displayItems.reduce((sum, d) => sum + d.displayAmount, 0);
    const amountStr = totalAmount.toFixed(2);

    const categoryLabel = getSubItemLabel(li.category, typeMeta);
    const description = li.description ?? '';
    const serviceDate = li.itemDate ? isoToDateDDMMYYYY(li.itemDate) : '';

    const fields = [
      jobCode,                                          // *InvoiceNo
      index === 0 ? escapeCsvValue(clientName) : '',       // *Customer
      index === 0 ? invoiceDate : '',                   // *InvoiceDate
      index === 0 ? dueDateStr : '',                    // *DueDate
      '',                                               // Terms
      '',                                               // Location
      '',                                               // Memo
      escapeCsvValue(categoryLabel),                    // Item(Product/Service)
      escapeCsvValue(description),                      // ItemDescription
      '1',                                              // ItemQuantity
      amountStr,                                        // ItemRate
      amountStr,                                        // *ItemAmount
      '',                                               // Shipping address
      '',                                               // Ship via
      '',                                               // Shipping date
      '',                                               // Tracking no.
      '',                                               // Shipping Charge
      serviceDate,                                      // Service Date
    ];

    return fields.join(',');
  });

  return [CSV_HEADER, ...rows].join('\n');
}

export function downloadQuickBooksCsv(params: QuickBooksCsvParams): void {
  const csv = generateQuickBooksCsv(params);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${params.jobCode}-quickbooks.csv`;
  link.click();
  URL.revokeObjectURL(url);
}
